import React from "react";
import { useBranding } from "../../context/BrandingContext";

/**
 * Low-stock urgency pill — "Only N left in stock".
 *
 * Renders NOTHING when:
 *   - The tenant has `low_stock_indicator_enabled = false` (default)
 *   - `stock` is null / undefined / <= 0 (out-of-stock is a separate UI state)
 *   - `stock > threshold` (there's plenty — no urgency)
 *
 * Config is tenant-scoped via `/api/settings/app`:
 *   - `low_stock_indicator_enabled` — master toggle
 *   - `low_stock_threshold` — inclusive cap (default 5)
 *
 * Usage:
 *   <LowStockPill stock={product.stock_total} />
 */
export default function LowStockPill({ stock, className = "" }) {
  const { branding } = useBranding();
  if (!branding?.low_stock_indicator_enabled) return null;
  const threshold = Number(branding.low_stock_threshold ?? 5);
  if (!Number.isFinite(threshold) || threshold <= 0) return null;
  const s = Number(stock);
  if (!Number.isFinite(s) || s <= 0 || s > threshold) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 ${className}`}
      data-testid="low-stock-pill"
      role="status"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
      Only {s} left in stock
    </span>
  );
}
